
import { App } from '@/types/App';

/**
 * Sorts an array of apps alphabetically by name (case-insensitive)
 * @param apps - Array of apps to sort
 * @returns Sorted array of apps
 */
export const sortAppsAlphabetically = (apps: App[]): App[] => {
  return [...apps].sort((a, b) => 
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );
};

/**
 * Deduplicates an array of apps by their ID
 * @param apps - Array of apps to deduplicate
 * @returns Deduplicated array of apps
 */
export const deduplicateApps = (apps: App[]): App[] => {
  const start = performance.now();
  const deduplicatedMap = new Map<string, App>();
  const duplicateIds = new Set<string>();
  
  apps.forEach(app => {
    if (!deduplicatedMap.has(app.id)) {
      deduplicatedMap.set(app.id, app);
    } else {
      duplicateIds.add(app.id);
    }
  });
  
  const deduplicatedApps = Array.from(deduplicatedMap.values());
  const end = performance.now();
  const duration = end - start;
  const duplicatesRemoved = apps.length - deduplicatedApps.length;
  
  // Enhanced logging with performance metrics and duplicate analysis
  if (duplicatesRemoved > 0) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    console.log(`[${timestamp}] DEDUPLICATION: ${apps.length} → ${deduplicatedApps.length} apps`, {
      duration: `${duration.toFixed(2)}ms`,
      duplicatesRemoved,
      duplicateIds: Array.from(duplicateIds),
      uniqueApps: deduplicatedApps.length,
      performanceImpact: duration > 10 ? 'HIGH' : duration > 5 ? 'MEDIUM' : 'LOW'
    });
    
    // Log sample of duplicate app names for debugging
    if (duplicateIds.size > 0) {
      const sampleDuplicates = Array.from(duplicateIds).slice(0, 3).map(id => {
        const app = apps.find(a => a.id === id);
        return app ? `${app.name} (${app.id})` : id;
      });
      console.log(`Sample duplicates:`, sampleDuplicates);
    }
  }
  
  return deduplicatedApps;
};
